import React, { useCallback, useEffect, useRef } from 'react';
import type { Subscription } from 'rxjs';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons/faCircleNotch';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { deleteOrderDocumentRequest, deleteOrderDocumentSuccess } from '../../../../../store/customs-documents/customs-documents.actions';
import { downloadDocument } from '../../../../../store/customs-documents/customs-documents.api';
import { getDocumentTypeLabel } from './document-type-dropdown';
import { messages } from './messages';
import { BLOB_URL_TTL_MS, ERROR_STATUSES } from './types';
import type { CustomsDocument } from './types';
import style from './customs-documents-section.module.less';

type OrderDocumentListItemProps = {
  salesOrderId: string;
  document: CustomsDocument;
};

// Prevent deletion while the file is still being uploaded / processed
const PROCESSING_STATUSES = ['uploading', 'processing', 'processing_claimed'];

const STATUS_MESSAGE_MAP: Record<string, MessageDescriptor> = {
  failed: messages.statusFailed,
  invalid: messages.statusInvalid,
  processing: messages.statusProcessing,
  processing_claimed: messages.statusProcessing,
};

/** Single document row for order-level (pre-ship) customs documents. */
export const OrderDocumentListItem = ({ salesOrderId, document }: OrderDocumentListItemProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { documentId, fileName, documentType, status, error } = document;
  const isProcessing = PROCESSING_STATUSES.includes(status);
  const isFileUploading = status === 'uploading';
  const isError = ERROR_STATUSES.includes(status);
  const statusMessage = STATUS_MESSAGE_MAP[status];

  const isLocalOnly = documentId?.startsWith('failed-');

  const handleDelete = useCallback(() => {
    if (isLocalOnly) {
      dispatch(deleteOrderDocumentSuccess(salesOrderId, documentId));
    } else {
      dispatch(deleteOrderDocumentRequest(salesOrderId, documentId));
    }
  }, [dispatch, salesOrderId, documentId, isLocalOnly]);

  const downloadSubRef = useRef<Subscription | null>(null);
  const blobCleanupRef = useRef<Array<{ url: string; timerId: ReturnType<typeof setTimeout> }>>([]);

  useEffect(
    () => () => {
      downloadSubRef.current?.unsubscribe();
      blobCleanupRef.current.forEach(({ url, timerId }) => {
        clearTimeout(timerId);
        URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const handlePreview = useCallback(() => {
    downloadSubRef.current?.unsubscribe();
    downloadSubRef.current = downloadDocument(documentId).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      const timerId = setTimeout(() => {
        URL.revokeObjectURL(url);
        blobCleanupRef.current = blobCleanupRef.current.filter((entry) => entry.url !== url);
      }, BLOB_URL_TTL_MS);
      blobCleanupRef.current.push({ url, timerId });
    });
  }, [documentId]);

  const itemClassName = [style['document-list-item'], isError && style['document-list-item--error']].filter(Boolean).join(' ');

  return (
    <>
      <FlexRow className={itemClassName} gap={Gap.Space8}>
        <FlexColumn className={style['document-info']}>
          <Button
            ariaLabel={intl.formatMessage(messages.previewAriaLabel, { fileName })}
            onClick={handlePreview}
            theme={THEME_CONSTANTS.link}
          >
            {fileName}
          </Button>
          {documentType && (
            <Text className={style['document-type-label']} textType={TextType.Caption} value={getDocumentTypeLabel(intl, documentType)} />
          )}
        </FlexColumn>

        {isFileUploading && (
          <FlexRow className={style['status-sending']} gap={Gap.Space4}>
            <FontAwesomeIcon icon={faCircleNotch} spin />
            <Text textType={TextType.Caption} value={messages.statusSending} />
          </FlexRow>
        )}

        {!isFileUploading && statusMessage && (
          <Text className={style[`status-badge--${status}`]} textType={TextType.Caption} value={statusMessage} />
        )}

        {!isProcessing && (
          <Button
            ariaLabel={intl.formatMessage(messages.deleteAriaLabel, { fileName })}
            className={style['delete-button']}
            onClick={handleDelete}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        )}
      </FlexRow>
      {isError && error && <Text className={style['error-message']} textType={TextType.Caption} value={error} />}
    </>
  );
};
